import JSZip from 'jszip';

import {
  buildAssetInventory,
  normalizeImportPath,
  type AssetInventoryInput,
} from './asset-inventory';
import { parseImportedDocument } from './document-parser';
import type {
  ImportedPageDocument,
  ImportIssue,
  WebsiteImportResult,
} from './import-types';
import { resolveImportReference } from './path-resolver';

const IGNORED_PATH_PARTS = new Set([
  '__MACOSX',
  '.DS_Store',
  'Thumbs.db',
]);

function shouldIgnoreEntry(entryPath: string): boolean {
  const normalizedPath = normalizeImportPath(entryPath);
  const parts = normalizedPath.split('/');

  return parts.some((part) => IGNORED_PATH_PARTS.has(part));
}

function isHtmlPath(entryPath: string): boolean {
  return /\.html?$/i.test(entryPath);
}

function isMarkdownPath(entryPath: string): boolean {
  return /\.md$/i.test(entryPath);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function markdownToHtml(markdown: string): string {
  const lines = markdown.replace(/\r\n?/g, '\n').split('\n');
  const html: string[] = [];
  let paragraph: string[] = [];
  let listType: 'ul' | 'ol' | null = null;

  const renderInline = (value: string): string =>
    escapeHtml(value)
      .replace(
        /!\[([^\]]*)\]\(([^)]+)\)/g,
        '<img src="$2" alt="$1">',
      )
      .replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        '<a href="$2">$1</a>',
      )
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/__([^_]+)__/g, '<strong>$1</strong>')
      .replace(/\*([^*]+)\*/g, '<em>$1</em>')
      .replace(/_([^_]+)_/g, '<em>$1</em>')
      .replace(/`([^`]+)`/g, '<code>$1</code>');

  const flushParagraph = () => {
    if (!paragraph.length) return;

    html.push(
      `<p>${renderInline(paragraph.join(' ').trim())}</p>`,
    );

    paragraph = [];
  };

  const closeList = () => {
    if (!listType) return;

    html.push(`</${listType}>`);
    listType = null;
  };

  for (const line of lines) {
    const trimmed = line.trim();

    if (!trimmed) {
      flushParagraph();
      closeList();
      continue;
    }

    const heading = /^(#{1,6})\s+(.+)$/.exec(trimmed);

    if (heading) {
      flushParagraph();
      closeList();

      const level = heading[1].length;

      html.push(
        `<h${level}>${renderInline(heading[2])}</h${level}>`,
      );

      continue;
    }

    const unorderedItem = /^[-*+]\s+(.+)$/.exec(trimmed);

    if (unorderedItem) {
      flushParagraph();

      if (listType !== 'ul') {
        closeList();
        html.push('<ul>');
        listType = 'ul';
      }

      html.push(
        `<li>${renderInline(unorderedItem[1])}</li>`,
      );

      continue;
    }

    const orderedItem = /^\d+\.\s+(.+)$/.exec(trimmed);

    if (orderedItem) {
      flushParagraph();

      if (listType !== 'ol') {
        closeList();
        html.push('<ol>');
        listType = 'ol';
      }

      html.push(
        `<li>${renderInline(orderedItem[1])}</li>`,
      );

      continue;
    }

    if (/^---+$/.test(trimmed)) {
      flushParagraph();
      closeList();
      html.push('<hr>');
      continue;
    }

    paragraph.push(trimmed);
  }

  flushParagraph();
  closeList();

  return html.join('\n');
}

function htmlPathFromMarkdownPath(sourcePath: string): string {
  const withoutExtension = sourcePath.replace(/\.md$/i, '');

  if (/(?:^|\/)index$/i.test(withoutExtension)) {
    return `${withoutExtension}.html`;
  }

  return `${withoutExtension}/index.html`;
}

function createDocumentFromMarkdown(input: {
  sourcePath: string;
  markdown: string;
}): ImportedPageDocument {
  const { sourcePath, markdown } = input;

  const firstHeading =
    /^#\s+(.+)$/m.exec(markdown)?.[1]?.trim() ?? '';

  const title =
    firstHeading ||
    sourcePath
      .split('/')
      .pop()
      ?.replace(/\.md$/i, '')
      .replace(/[-_]+/g, ' ')
      .trim() ||
    'Imported Page';

  const sourceDocument = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>${escapeHtml(title)}</title>
</head>
<body>
  <main data-panderr-import-source="markdown">
${markdownToHtml(markdown)}
  </main>
</body>
</html>`;

  return parseImportedDocument({
    sourcePath: htmlPathFromMarkdownPath(sourcePath),
    sourceDocument,
  });
}

function sortPages(
  pages: ImportedPageDocument[],
): ImportedPageDocument[] {
  return [...pages].sort((left, right) => {
    if (left.route === '/') return -1;
    if (right.route === '/') return 1;

    return left.route.localeCompare(right.route);
  });
}

function collectMissingAssetIssues(
  pages: ImportedPageDocument[],
  availablePaths: Set<string>,
): ImportIssue[] {
  const issues: ImportIssue[] = [];
  const reported = new Set<string>();

  for (const page of pages) {
    for (const assetPath of page.assetPaths) {
      const resolved = resolveImportReference(
        page.sourcePath,
        assetPath,
      );

      if (resolved.kind === 'unsafe') {
        const issueKey =
          `unsafe:${page.sourcePath}:${assetPath}`;

        if (reported.has(issueKey)) continue;
        reported.add(issueKey);

        issues.push({
          type: 'unsafe-path',
          severity: 'error',
          pagePath: page.sourcePath,
          value: assetPath,
          message:
            'This reference escapes the website ZIP root.',
        });

        continue;
      }

      if (resolved.kind !== 'local') {
        continue;
      }

      if (availablePaths.has(resolved.resolvedPath)) {
        continue;
      }

      const issueKey =
        `missing:${page.sourcePath}:${resolved.resolvedPath}`;

      if (reported.has(issueKey)) continue;
      reported.add(issueKey);

      issues.push({
        type: 'missing-asset',
        severity: 'warning',
        pagePath: page.sourcePath,
        value: assetPath,
        message:
          `Referenced asset was not found: ${resolved.resolvedPath}`,
      });
    }
  }

  return issues;
}

export async function importWebsiteZip(
  zipInput: ArrayBuffer | Uint8Array,
): Promise<WebsiteImportResult> {
  const zip = await JSZip.loadAsync(zipInput);

  const pages: ImportedPageDocument[] = [];
  const assetInputs: AssetInventoryInput[] = [];
  const issues: ImportIssue[] = [];

  const entries = Object.values(zip.files)
    .filter((entry) => !entry.dir)
    .filter((entry) => !shouldIgnoreEntry(entry.name))
    .sort((left, right) =>
      left.name.localeCompare(right.name),
    );

  for (const entry of entries) {
    const normalizedPath = normalizeImportPath(entry.name);

    if (!normalizedPath) {
      continue;
    }

    if (isHtmlPath(normalizedPath)) {
      try {
        const sourceDocument = await entry.async('string');

        pages.push(
          parseImportedDocument({
            sourcePath: normalizedPath,
            sourceDocument,
          }),
        );
      } catch (error) {
        issues.push({
          type: 'parse-error',
          severity: 'error',
          pagePath: normalizedPath,
          value: normalizedPath,
          message:
            error instanceof Error
              ? error.message
              : 'The HTML document could not be parsed.',
        });
      }

      continue;
    } if (isMarkdownPath(normalizedPath)) {
  try {
    const markdown = await entry.async('string');

    pages.push(
      createDocumentFromMarkdown({
        sourcePath: normalizedPath,
        markdown,
      }),
    );
  } catch (error) {
    issues.push({
      type: 'parse-error',
      severity: 'error',
      pagePath: normalizedPath,
      value: normalizedPath,
      message:
        error instanceof Error
          ? error.message
          : 'The Markdown document could not be imported.',
    });
  }

  continue;
}
    const bytes = await entry.async('uint8array');

    assetInputs.push({
      path: normalizedPath,
      bytes,
    });
  }

  const assets = buildAssetInventory(assetInputs);

  const availablePaths = new Set([
    ...pages.map((page) =>
      normalizeImportPath(page.sourcePath),
    ),
    ...assets.map((asset) => asset.normalizedPath),
  ]);

  issues.push(
    ...collectMissingAssetIssues(pages, availablePaths),
  );

  return {
    pages: sortPages(pages),
    assets,
    issues,
  };
}
